import type {
  ChannelAndUserInChannelMetadata,
  ExperienceMetadata,
  UserMetadata,
} from "@/models";
import type { User } from "@/generated/graphql";

export enum TrackEventEnum {
  Login = "Login",
  RegistrationFinished = "Registration Finished",
  FanIdentified = "Fan Identified",
  EventJoined = "Event Joined",
  EventLeft = "Event Left",
  RaisedHand = "Raised Hand",
  StartPresentation = "Start Presentation",
  StopPresentation = "Stop Presentation",
  FanBarJoined = "Fan Bar Joined",
  StageJoined = "Stage Joined",
  StageLeft = "Stage Left",
  SetUserVIP = "Set User VIP",
  GenerateShareLink = "Generate Share Link",
  TopicWatched = "Topic Watched",
  OfferWatched = "Offer Watched",
  OfferClicked = "Offer Clicked",
  ChatMessage = "Chat Message",
  EventStarted = "Event Started",
  EventEnded = "Event Ended",
  ShowViewed = "Live Show Viewed",
  ShowScheduledViewed = "Scheduled Show Viewed",
  ReplayViewed = "Replay Viewed",
  MemberListClicked = "Member List Clicked",

  // Amplitude
  LoginClicked = "LoginClicked",
  LoginStartedEnteringInformation = "LoginStartedEnteringInformation",
  UpgradeTriggeredByGatingEvent = "UpgradeTriggeredByGatingEvent",
  UserRegistered = "UserRegistered",
  LoginTriggeredByGatingEvent = "LoginTriggeredByGatingEvent",
  LoginFailed = "LoginFailed",
  LoginEnteredInformation = "LoginEnteredInformation",
  LoginAuthenticated = "LoginAuthenticated",
  SubscriptionTierSelection = "SubscriptionTierSelection",
  SubscriptionCompleted = "SubscriptionCompleted",
  SubscriptionCanceled = "SubscriptionCanceled",
  SubscriptionUpgraded = "SubscriptionUpgraded",
  SubscriptionDowngraded = "SubscriptionDowngraded",
  SubscriptionCancelled = "SubscriptionCancelled",
  SubscriptionReactivated = "SubscriptionReactivated",
  CommunitySelected = "CommunitySelected",
  UserProfileUpdated = "UserProfileUpdated",
  UserProfilePhotoAdded = "UserProfilePhotoAdded",
  UserProfilePhotoUpdated = "UserProfilePhotoUpdated",
  ChannelCreated = "ChannelCreated",
  ThreadCreated = "ThreadCreated",
  ThreadUpdated = "ThreadUpdated",
  ThreadCommented = "ThreadCommented",
  ThreadLiked = "ThreadLiked",
  ShowCreated = "ShowCreated",
  ShowAddedAgenda = "ShowAddedAgenda",
  ShowPreparationStarted = "ShowPreparationStarted",
  ShowStarted = "ShowStarted",
  ShowDuplicated = "ShowDuplicated",
  ShowEnded = "ShowEnded",
  ShowReplayViewed = "ShowReplayViewed",
  ShowChatMessageSent = "ShowChatMessageSent",
  ShowChatMessageLiked = "ShowChatMessageLiked",
  ReplayChatMessageSent = "ReplayChatMessageSent",
  ReplayChatMessageLiked = "ReplayChatMessageLiked",
  ShowLiked = "ShowLiked",
  FanScoreUpdated = "FanScoreUpdated",
  ShowViewRegistered = "ShowViewRegistered",
  HostInvited = "HostInvited",
  HostRegistered = "HostRegistered",
  ShowCreatedByHost = "ShowCreatedByHost",
  ShowCompletedByHost = "ShowCompletedByHost",
  RegisteredUserPerShow = "RegisteredUserPerShow",
  ShowSessionLengthTracked = "ShowSessionLengthTracked",
  SessionLengthTracked = "SessionLengthTracked",
  LiveQuizCreated = "LiveQuizCreated",
  LiveQuizAnswered = "LiveQuizAnswered",
  LiveRaffleCreated = "LiveRaffleCreated",
  LiveRaffleEntered = "LiveRaffleEntered",
  ChannelQuizCreated = "ChannelQuizCreated",
  ChannelQuizAnswered = "ChannelQuizAnswered",
  ChannelQuizWon = "ChannelQuizWon",
  ChannelRaffleCreated = "ChannelRaffleCreated",
  ChannelRaffleEntered = "ChannelRaffleEntered",
  ChannelRaffleWon = "ChannelRaffleWon",
  GenerateReferralLink = "GenerateReferralLink",
  VisitFromReferralLink = "VisitFromReferralLink",
  ReferralLinkConversion = "ReferralLinkConversion",
  OfferClaimed = "OfferClaimed",
  ConnectWalletTriggered = "Connect Wallet Triggered",
  ConnectWalletSuccess = "Connect Wallet Success",
  TopUpWalletTriggered = "Top Up Wallet Triggered",
  TopUpPurchaseFlowStarted = "Top Up Purchase Flow Started",
  TopUpPurchaseFlowCompleted = "Top Up Purchase Flow Completed",
  TopUpPurchaseFlowClosed = "Top Up Purchase Flow Closed",
}

export enum EventCategory {
  Engagement = "engagement",
  Growth = "growth",

  // Amplitude
  Identity = "identity",
  Subscription = "subscription",
  UserProfile = "user profile",
  Channels = "channels",
  Threads = "threads",
  ShowCreation = "show creation",
  ShowProduction = "show production",
  LiveShow = "live show",
  Replays = "replays",
  ChatReactions = "chat/reactions",
  FanScoreXP = "fan score/XP",
  Registration = "registration",
  Community = "community",
  Show = "show",
  LiveQuiz = "live quiz",
  ChannelQuiz = "channel quiz",
  ChannelRaffle = "channel raffle",
  LiveRaffle = "live raffle",
  NonLiveRaffles = "non-live raffles",
  LiveContests = "live contests",
}

export type TrackEventWithMetadata = {
  [TrackEventEnum.LoginClicked]: {};
  [TrackEventEnum.UserRegistered]: ExperienceMetadata;
  [TrackEventEnum.LoginTriggeredByGatingEvent]: ExperienceMetadata;
  [TrackEventEnum.UpgradeTriggeredByGatingEvent]: ExperienceMetadata;
  [TrackEventEnum.LoginStartedEnteringInformation]: ExperienceMetadata;
  [TrackEventEnum.LoginFailed]: {
    error: string;
  } & ExperienceMetadata;
  [TrackEventEnum.LoginEnteredInformation]: {
    userPhone: string;
  } & ExperienceMetadata;
  [TrackEventEnum.LoginAuthenticated]: ChannelAndUserInChannelMetadata &
    UserMetadata &
    ExperienceMetadata;
  [TrackEventEnum.SubscriptionTierSelection]: {
    subscriptionId: string;
    subscriptionTier: string;
    subscriptionType: string;
  } & ExperienceMetadata;
  [TrackEventEnum.SubscriptionCompleted]: {};
  [TrackEventEnum.CommunitySelected]: {
    selectedCommunityIds: string[];
    selectedCommunityNames: string[];
  };
  [TrackEventEnum.SubscriptionUpgraded]: ExperienceMetadata & {
    previousSubscriptionTier?: string;
    newSubscriptionTier?: string;
  };
  [TrackEventEnum.SubscriptionDowngraded]: {
    previousSubscriptionTier?: string;
    newSubscriptionTier?: string;
  };
  [TrackEventEnum.SubscriptionReactivated]: ExperienceMetadata & {
    previousSubscriptionTier?: string;
    newSubscriptionTier?: string;
  };
  [TrackEventEnum.SubscriptionCanceled]: {
    previousSubscriptionTier?: string;
    newSubscriptionTier?: string;
  };
  [TrackEventEnum.UserProfileUpdated]: {
    fieldsUpdated: string[];
  };
  [TrackEventEnum.UserProfilePhotoAdded]: {
    avatarUrl: string;
  };
  [TrackEventEnum.UserProfilePhotoUpdated]: {
    avatarUrl: string;
  };
  [TrackEventEnum.ChannelCreated]: {
    channelId?: string;
    hostId?: string;
  };
  [TrackEventEnum.ThreadCreated]: ExperienceMetadata;
  [TrackEventEnum.ThreadUpdated]: ExperienceMetadata;
  [TrackEventEnum.ThreadCommented]: ExperienceMetadata;
  [TrackEventEnum.ThreadLiked]: {
    likeOrUnlike: "like" | "unlike";
  } & ExperienceMetadata;
  [TrackEventEnum.ShowCreated]: ExperienceMetadata;
  [TrackEventEnum.ShowDuplicated]: ExperienceMetadata;
  [TrackEventEnum.ShowAddedAgenda]: {
    agenda?: string;
  } & ExperienceMetadata;
  [TrackEventEnum.ShowPreparationStarted]: ExperienceMetadata;
  [TrackEventEnum.ShowStarted]: ExperienceMetadata;
  [TrackEventEnum.ShowEnded]: ExperienceMetadata;
  [TrackEventEnum.ShowViewed]: ExperienceMetadata;
  [TrackEventEnum.ShowScheduledViewed]: ExperienceMetadata;
  [TrackEventEnum.ShowReplayViewed]: {
    showReplayUrl: string;
  } & ExperienceMetadata;
  [TrackEventEnum.ShowChatMessageSent]: {
    isReply: boolean;
  } & ExperienceMetadata;
  [TrackEventEnum.ShowChatMessageLiked]: {
    likeOrUnlike: "like" | "unlike";
  } & ExperienceMetadata;
  [TrackEventEnum.ReplayChatMessageSent]: {
    isReply: boolean;
  } & ExperienceMetadata;
  [TrackEventEnum.ReplayChatMessageLiked]: {
    likeOrUnlike: "like" | "unlike";
  } & ExperienceMetadata;
  [TrackEventEnum.ShowLiked]: ExperienceMetadata;
  [TrackEventEnum.FanScoreUpdated]: {
    pointsScored: number;
    experienceId?: string | null;
    experienceType?: string | null;
  };
  [TrackEventEnum.ShowViewRegistered]: ExperienceMetadata;
  [TrackEventEnum.ChannelCreated]: {
    channelId?: string;
    hostId?: string;
  };
  [TrackEventEnum.HostInvited]: {
    hostId?: string;
    invitedByUserId?: string;
  };
  [TrackEventEnum.HostRegistered]: {
    hostId?: string;
    registrationDate?: string;
  };
  [TrackEventEnum.ShowCreatedByHost]: {
    showId?: string;
    hostId?: string;
  };
  [TrackEventEnum.ShowCompletedByHost]: {
    showId?: string;
    hostId?: string;
  };
  [TrackEventEnum.RegisteredUserPerShow]: {
    showId?: string;
    registrationCount?: number;
  };
  [TrackEventEnum.ShowSessionLengthTracked]: {
    userId?: string;
    sessionLength?: string;
  };
  [TrackEventEnum.SessionLengthTracked]: {
    userId?: string;
    sessionLength?: string;
  };
  [TrackEventEnum.LiveQuizCreated]: {
    quizId: string;
  } & ExperienceMetadata;
  [TrackEventEnum.LiveQuizAnswered]: {
    quizId?: string;
    questionId?: string;
    questionLabel?: string;
    answerId?: string;
    answerLabel?: string;
  } & ExperienceMetadata;
  [TrackEventEnum.LiveRaffleCreated]: {
    raffleId?: string;
    raffleTitle?: string;
    prize?: string;
  } & ExperienceMetadata;
  [TrackEventEnum.LiveRaffleEntered]: {
    raffleId?: string;
    raffleTitle?: string;
  } & ExperienceMetadata;
  [TrackEventEnum.ChannelQuizCreated]: ExperienceMetadata;
  [TrackEventEnum.ChannelQuizAnswered]: {
    quizId?: string;
    questionId?: string;
    questionLabel?: string;
    answerId?: string;
    answerLabel?: string;
  } & ExperienceMetadata;
  [TrackEventEnum.ChannelQuizWon]: ExperienceMetadata;
  [TrackEventEnum.ChannelRaffleCreated]: {
    prize?: string;
  } & ExperienceMetadata;
  [TrackEventEnum.ChannelRaffleEntered]: {
    prize?: string;
  } & ExperienceMetadata;
  [TrackEventEnum.ChannelRaffleWon]: {
    prize?: string;
    winnerIds?: string[];
    winnerUsernames?: string[];
  } & ExperienceMetadata;
  [TrackEventEnum.GenerateReferralLink]: ExperienceMetadata;
  [TrackEventEnum.VisitFromReferralLink]: ExperienceMetadata & {
    referrer?: User;
  };
  [TrackEventEnum.ReferralLinkConversion]: ExperienceMetadata & {
    referrer?: User;
  };
  [TrackEventEnum.OfferClaimed]: ExperienceMetadata & {
    offerId?: string;
    offerDescription?: string;
    offerUrl?: string;
  };
  [TrackEventEnum.ConnectWalletTriggered]: {};
  [TrackEventEnum.ConnectWalletSuccess]: {};
  [TrackEventEnum.TopUpWalletTriggered]: {
    tokenSymbol: string;
    tokenAmount: number;
  };
  [TrackEventEnum.TopUpPurchaseFlowStarted]: {
    tokenSymbol: string;
    tokenAmount: number;
  };
  [TrackEventEnum.TopUpPurchaseFlowCompleted]: {
    tokenSymbol: string;
    tokenAmount: number;
  };
  [TrackEventEnum.TopUpPurchaseFlowClosed]: {
    tokenSymbol: string;
    tokenAmount: number;
  };
};

export type TrackEventMetadata = {
  [key in TrackEventEnum]: key extends keyof TrackEventWithMetadata
    ? TrackEventWithMetadata[key]
    : never;
};

export type TrackOptions<T extends TrackEventEnum> =
  TrackEventMetadata[T] extends never
    ? {
        event: T;
        category: EventCategory;
      }
    : {
        event: T;
        category: EventCategory;
        metadata: TrackEventMetadata[T];
      };
